// Bot AI: pacing, fatigue, wheel following, pulls, placement on the road and item use.
import { trackAt, trackGap, mod, LANE_W, ROAD_EDGE } from './track.js';
import { BIKE_KG, BIKE_LEN, INERTIA, clamp, clearance, combineShelter, resistance, airFactor, wakeShelter } from './physics.js';
import { wbalStep } from './signals.js';
import type { BotSpecialty, BotAI, Rider } from './race-types.js';
import type { Race } from './race.js';

/** Anaerobic reserve per kg (J) and multiplier per specialty: a sprinter has more punch, a climber less. */
const WPRIME_PER_KG = 280;
const WPRIME_SPEC: Record<BotSpecialty, number> = { sprinteur: 1.3, puncheur: 1.15, rouleur: 1, grimpeur: 0.9 };
/** Maximum 5 s power relative to FTP (Coggan power profiles: ~3.5 on average). */
const PMAX_RATIO = 3.5;
const PMAX_SPEC: Record<BotSpecialty, number> = { sprinteur: 4.2, puncheur: 3.8, rouleur: 3.3, grimpeur: 3 };
/** Distance (m) at which a bot launches its final sprint. */
const SPRINT_LAUNCH: Record<BotSpecialty, number> = { sprinteur: 260, puncheur: 330, rouleur: 220, grimpeur: 180 };
/** Lateral reach of a crate (lanes): the race's pickup tolerance (Race.step). */
const BOX_REACH = 0.7;
/**
 * The wheel. `gap`: target gap on it (m, axle to axle); `pull`: how fast a gap is closed (m/s per m off it); `range`: the
 * horizon for a wheel (m); `pass`: the horizon within which a rider on a line blocks a bot from moving onto it (m);
 * `feather`: braking (m/s²) per m/s over the wheel's pace when about to overlap it (bounded by physics FEATHER).
 */
export const WHEEL = { gap: 1.9, pull: 0.8, range: 12, pass: 12, feather: 2 };
/**
 * Moving up. A bot held back by its wheel waits `wait` s, plus up to `patience` for a placid temperament, then moves to a
 * line beside with `room` m free ahead (a wheel's gap and a bike's length: room to gain at least a bike), and holds that
 * aim for `hold` s while it comes up on the next wheel.
 */
export const MOVE_UP = { wait: 0.5, patience: 2.5, room: 1.9 + BIKE_LEN + 0.6, hold: 3 };
/**
 * The slot. A rider ahead within `SLOT` of the bot's line (units, about 1 m) is the wheel it rides behind, so a bot sitting
 * between two wheels holds its place instead of riding up level with them; only a rider further over sets the pace of a move up (PACE).
 */
export const SLOT = 0.3;
/**
 * Nobody lets a wheel go. Off the wheel by more than `from` m, a bot closes at up to `speed` m/s faster than it, pushing up
 * to `boost` times its FTP for the moment it takes, while its anaerobic reserve is above `reserve`: a gap that opens in a
 * bunch is shut with a dig, the way a rider who sees the wheel going kicks rather than ride tempo and lose it for good.
 */
export const CLOSE = { from: 2.4, speed: 1, boost: 1.6, reserve: 0.5 };
/**
 * Pulls: extra effort at the front of a small group, base duration (s), effort and duration of the recovery after pulling
 * off. At the front of a bunch the effort is tempo (`bunch`): nobody drives a fifty-rider peloton at full pull, the pace is
 * one the bunch can follow in the shelter, which is what keeps it together.
 */
export const PULL = { boost: 1.12, bunch: 1, base: 10, spread: 20, ease: 0.85, rest: 6 };
/**
 * Room on the road (units of LANE_W, 2.4 m). A rider less than `block` to the side is in the way (about 70 cm, shoulder
 * to shoulder in a bunch); a free place is looked for every `step` from the bot's own line, at most `reach` away (about
 * 2 m): nobody crosses the whole road to pass someone.
 */
export const ROOM = { block: 0.3, step: 0.3, reach: 0.9 };
/** Where a bot likes to sit behind a wheel, a little to one side, to see the road (15 to 36 cm): fixed per bot. */
export const SIDE = { min: 0.06, max: 0.15 };
/**
 * Nobody holds a line to the centimetre, but nobody wobbles either: the line wanders on two slow waves of a period of
 * `slow` then `slower` seconds (phases and periods drawn per bot), within `max`.
 */
export const SWAY = { max: 0.08, slow: [9, 17], slower: [19, 34] };
/**
 * In a bunch a rider does not sprint up the flank of the rider just ahead and beside them: within `range` m and `width`
 * across, they ride that rider's speed plus at most `surge` m/s, which is how a pack moves up: slowly, along its sides.
 */
export const PACE = { range: 9, width: 1, surge: 0.45 };
/**
 * Placement in a bunch, looked at every `every` to `every`+`spread` s. Each place tried `reach` steps of `grain` around
 * the aim, and three steps of `fine` either side of it (centring on a wheel to the centimetre), is scored: riders within a bike length wanting their room beside (`crowd`, a quartic bell of the room they are
 * owed, physics clearance: in full when level, less when overlapping, so the bunch staggers), shelter it gives (`shelter`),
 * and the move itself (`move`); a trim (three steps of `fine` at most) needs a gain of `trim`, a move the full `gain`. Nothing
 * pulls a bot towards the middle of the bunch: the road's width is the bunch's, and a pull to its axis only made it narrow and long. The aim changes only for a gain of `gain`: a bot does not keep
 * changing its mind. A bot is in a bunch (`bunch` riders within `around` m, ahead or behind): it then takes the bends with it.
 */
export const SETTLE = {
  every: 1,
  spread: 1,
  grain: 0.15,
  reach: 6,
  fine: 0.05,
  trim: 0.15,
  crowd: 3,
  shelter: 6,
  move: 1,
  gain: 0.6,
  ahead: 14,
  behind: 4,
  bunch: 3,
  around: 10,
  aims: true,
};
/** A bunch takes a bend together, towards its inside: units across per unit of curvature ahead, the bound, how fast (1/s). */
/** The wheel moved this far from a bot's aim (units): it looks again within `look` s, and may go straight to that wheel (`bunch`: in a bunch too). */
export const REACT = { far: 0.4, look: 0.35, moving: 0.2, bunch: false };
export const BEND = { gain: 22, max: 0.35, rate: 0.6, look: [8, 16, 28] };

// ---- Bot AI ----
export function botThink(race: Race, r: Rider, dt: number) {
  const tr = race.track,
    ai = r.ai!;
  const grade = trackAt(tr, r.s).grade;
  let k = 0.9 + clamp(grade * 5, -0.35, 0.4);
  const toLine = r.goal * tr.lap - r.s,
    spec = ai.spec;
  const R = race.rules,
    finale = R.finish === 'laps' && !R.ghost && r.finishT == null && toLine < 450;
  // lead-out for the sprint, then all-out sprint at the distance specific to the specialty
  const sprinting = finale && toLine < SPRINT_LAUNCH[spec ?? 'rouleur'];
  let contest = finale;
  if (finale) k += 0.35 + (spec === 'sprinteur' ? 0.25 : spec === 'puncheur' ? 0.1 : 0);
  if (spec === 'grimpeur') k += grade > 0.03 ? 0.12 : -0.03;
  else if (spec === 'sprinteur') k += grade > 0.04 ? -0.08 : 0;
  else if (spec === 'rouleur') k += Math.abs(grade) < 0.02 ? 0.06 : 0;
  else if (spec === 'puncheur') k += grade > 0.05 ? 0.1 : 0;
  for (const c of race.cps) {
    const d = c.s - r.s;
    if (d <= 0 || d > 300 || c.order.includes(r.id)) continue;
    if (c.kind === 'sprint') k += spec === 'sprinteur' ? 0.32 : spec === 'puncheur' ? 0.12 : 0.04;
    else k += spec === 'grimpeur' ? 0.25 : spec === 'puncheur' ? 0.14 : 0.03;
    contest = true;
  }
  const h = R.rubberBand || R.pace === 'group' ? race.nearestHuman(r) : null;
  if (h) {
    const gap = h.s - r.s,
      group = R.pace === 'group',
      band = R.arena || group ? 12 : 40,
      cap = R.arena ? 0.3 : group ? 0.25 : 0.14;
    if (gap > band) k += Math.min(cap, gap / 900);
    if (gap < -band) k -= Math.min(cap, -gap / 700);
  }
  if (R.eliminateLast) {
    const act = race.active();
    if (act.length > 1 && act.reduce((m, o) => (o.s < m.s ? o : m)) === r) k += 0.12;
  }
  if (r.finishT != null) k = 0.55;
  ai.noise += (-ai.noise * 0.3 + (race.rnd() - 0.5) * 0.5) * dt;
  let p = sprinting ? ai.pmax * (0.88 + ai.noise * 0.2) : Math.max(0, r.ftp * k * (1 + ai.noise));
  // on a wheel, only push what is needed to keep up: the shelter is for saving energy, not riding faster;
  // at the front with riders behind, take a hard pull then pull off to recover
  if (!contest && !sprinting) p = relay(race, r, holdWheel(race, r, p, dt), dt);
  else {
    ai.stuck = 0;
    ai.pullT = 0;
  }
  // fatigue: three-parameter model (Morton): full reserve, up to pmax; empty, no more than FTP.
  // Outside sprints, when the reserve drops, the bot eases off towards 95% of FTP to recover.
  // Group-ride pacemakers are exempt from fatigue: they must stay with the group.
  if (R.pace !== 'group') {
    if (!sprinting) {
      const floor = r.ftp * 0.95,
        f = clamp((ai.w / ai.wp - 0.1) / 0.4, 0, 1);
      if (p > floor) p = floor + (p - floor) * f;
    }
    p = Math.min(p, r.ftp + (ai.pmax - r.ftp) * (ai.w / ai.wp));
    ai.w = wbalStep(ai.w, p, r.ftp, ai.wp, dt);
  } else p = Math.min(p, ai.pmax);
  r.power = Math.max(0, p);
  r.cadence = clamp(88 - grade * 200 + ai.noise * 30, 60, 105);

  // the line wanders slowly; a bunch leans towards the inside of the bend ahead, all together
  const [p0, p1, t0, t1] = ai.wander,
    T = race.t * 2 * Math.PI;
  ai.sway = SWAY.max * (0.6 * Math.sin(T / t0 + p0) + 0.4 * Math.sin(T / t1 + p1));
  let bend = 0;
  if (ai.bunch) {
    for (const d of BEND.look) bend += tr.curv[Math.floor(mod(r.s + d, tr.L) / tr.step) % tr.N];
    bend = clamp((bend / BEND.look.length) * BEND.gain, -BEND.max, BEND.max);
  }
  ai.bend += (bend - ai.bend) * Math.min(1, dt * BEND.rate);

  // the wheel in hand moved well over: look sooner, a follower reacts in a fraction of a second. Not in a bunch, where
  // another wheel is right there: a pack is a lattice of places, not chains of followers, or every move would ripple down it
  if (
    (REACT.bunch || !ai.bunch) &&
    ai.wheel &&
    !(ai.wheel.ai && ai.wheel.ai.restT > 0) &&
    Math.abs(ai.wheel.latV) > REACT.moving &&
    Math.abs(ai.wheel.targetLane - r.targetLane) > REACT.far
  )
    ai.laneT = Math.min(ai.laneT, REACT.look);
  ai.laneT -= dt;
  if (ai.laneT <= 0) {
    ai.laneT = SETTLE.every + race.rnd() * SETTLE.spread;
    const ahead = (lane: number) => hazardAhead(race, r, lane);
    if (ahead(r.targetLane)) {
      const opts = spots(r).filter(l => !ahead(l));
      if (opts.length) r.targetLane = opts[0];
    } else if (r.item || r.itemRoll || !race.boxes.length || !cratePlace(race, r)) settle(race, r);
  }

  if (r.item) {
    ai.hold += dt;
    const it = r.item,
      front = race.nearestAhead(r, 80),
      hold = ai.hold * ai.aggr;
    let use: boolean;
    if (it === 'shell') use = !!front || hold > 6;
    else if (it === 'banana') use = race.riders.some(o => o !== r && !o.out && r.s - o.s > 2 && r.s - o.s < 20 && Math.abs(o.lane - r.lane) < 0.8) || hold > 8;
    else if (it === 'shield') use = race.shells.some(sh => sh.target === r) || hold > 5;
    else if (it === 'turbo' || it === 'bomb') use = grade > 0.03 || hold > 4;
    else if (it === 'magnet') use = !!race.nearestAhead(r, 25) || hold > 3;
    else if (it.startsWith('c:')) {
      const C = race.custom.get(it)?.content;
      use = !C
        ? true
        : C.target === 'self'
          ? grade > 0.03 || hold > 3
          : C.delivery === 'trap'
            ? race.riders.some(o => o !== r && !o.out && r.s - o.s > 2 && r.s - o.s < 20) || hold > 6
            : !!front || hold > 5;
    } else use = hold > 1.5;
    if (use) {
      race.useItem(r);
      ai.hold = 0;
    }
  }
}

/** Signed gap along the loop from `r` to `o` (positive: `o` is ahead). */
const gapOf = (race: Race, r: Rider, o: Rider) => trackGap(race.track, r.s, o.s);

/** Where a rider is aiming: their chosen place plus, for a bot, the wander of its line and the bunch's bend. */
export const aimOf = (r: { targetLane: number; ai?: { sway: number; bend: number } | null }): number =>
  r.ai ? clamp(r.targetLane + r.ai.sway + r.ai.bend, -ROAD_EDGE - SWAY.max, ROAD_EDGE + SWAY.max) : r.targetLane;

/** The place a rider holds on the road: a bot's chosen line (its lane also carries the bunch's lean and its wander), a human's position. */
const placeOf = (o: Rider): number => (o.ai && SETTLE.aims ? o.targetLane : o.lane);

/** Whether rider `o` is on line `lane` (within ROOM.block), where it rides or, with `heading`, where it is going. */
const onLine = (o: Rider, lane: number, heading: boolean, width = ROOM.block): boolean =>
  Math.abs(o.lane - lane) < width || (heading && Math.abs(o.targetLane - lane) < width);

/**
 * Nearest rider just ahead on the same line (gap along the loop), within the given horizon. When choosing a line to
 * move to (`heading`), a rider moving onto it counts as on it already: two bots never pick the same spot at once.
 */
function wheelAhead(race: Race, r: Rider, range: number, lane = r.lane, heading = false, width = ROOM.block): { o: Rider; gap: number } | null {
  let best: { o: Rider; gap: number } | null = null;
  for (const o of race.riders) {
    if (o === r || o.out || o.fx.crash) continue;
    const gap = gapOf(race, r, o);
    // as soon as a wheel overlaps from behind, it blocks: you cannot ride through a rider
    if (gap > 0 && gap < range && onLine(o, lane, heading, width) && (!best || gap < best.gap)) best = { o, gap };
  }
  return best;
}

/** Whether a rider is alongside line `lane`, or moving onto it, less than `within` m ahead or behind. */
function alongside(race: Race, r: Rider, lane: number, within: number): boolean {
  return race.riders.some(o => o !== r && !o.out && onLine(o, lane, true) && Math.abs(gapOf(race, r, o)) < within);
}

/** Free places a bot may move to, nearest first: every ROOM.step from its own line, out to the edges of the road. */
export function spots(r: Rider): number[] {
  const out: number[] = [];
  for (let k = 1; k * ROOM.step <= 2 * ROAD_EDGE + 1e-9; k++) {
    for (const l of [r.targetLane - k * ROOM.step, r.targetLane + k * ROOM.step]) {
      const c = clamp(l, -ROAD_EDGE, ROAD_EDGE);
      if (Math.abs(c - r.targetLane) > ROOM.step / 2 && !out.some(x => Math.abs(x - c) < 1e-6)) out.push(c);
    }
  }
  // the road's edge may bring a place closer than the step: keep them nearest first (stable, left first on a tie)
  return out.sort((a, b) => Math.abs(a - r.targetLane) - Math.abs(b - r.targetLane));
}

/** A crate is taken from anywhere within BOX_REACH of it: move only when none is in reach, and then just far enough. Whether the bot moved. */
function cratePlace(race: Race, r: Rider): boolean {
  const L = race.track.L,
    bx = race.boxes.filter(b => b.cd <= 0 && mod(b.s - r.s, L) < 60 && mod(b.s - r.s, L) > 3);
  if (!bx.length || bx.some(b => Math.abs(b.lane - r.targetLane) < BOX_REACH - SWAY.max)) return false;
  const near = bx.sort((a, b) => Math.abs(a.lane - r.lane) - Math.abs(b.lane - r.lane))[0];
  if (Math.abs(near.lane - r.targetLane) > BOX_REACH + ROOM.reach) return false; // not worth a ride across the road
  r.targetLane = clamp(near.lane + Math.sign(r.targetLane - near.lane) * (BOX_REACH - SWAY.max - 0.05), -ROAD_EDGE, ROAD_EDGE);
  return true;
}

/**
 * A bot's place in the bunch (see SETTLE): the best of the places around its aim, kept unless another is clearly better.
 * Alone, it holds its line. Riding past someone or recovering from a pull, it stays on the line it chose.
 */
function settle(race: Race, r: Rider) {
  const ai = r.ai!,
    R = race.rules,
    mates = R.teams?.draftTeamOnly;
  const close: { o: Rider; gap: number }[] = [];
  let around = 0;
  for (const o of race.riders) {
    if (o === r || o.out) continue;
    const gap = gapOf(race, r, o);
    if (Math.abs(gap) < SETTLE.around) around++;
    if (gap >= -SETTLE.behind && gap <= SETTLE.ahead) close.push({ o, gap });
  }
  ai.bunch = around >= SETTLE.bunch; // the rider at the front of a bunch has it all behind
  if (!close.length || ai.passT > 0 || ai.restT > 0) return;
  const here = r.targetLane;
  /**
   * The worth of line `l` (lower is better): see SETTLE. Places are the ones chosen (a bot's aim), not the lanes ridden:
   * the bunch's lean into a bend and the wander of its lines would otherwise feed back into where everyone wants to be.
   */
  const score = (l: number): number => {
    let crowd = 0;
    const parts: number[] = [];
    for (const { o, gap } of close) {
      const p = placeOf(o);
      // a rider within a bike length wants room beside: all of it level, less the more the two overlap (physics clearance);
      // and nobody rides through a rider level with them to reach the other side
      if (Math.abs(gap) < BIKE_LEN) {
        if ((p - here) * (p - l) < 0) return Infinity;
        const d = (Math.abs(p - l) * LANE_W) / clearance(gap);
        crowd += Math.exp(-(d * d) * (d * d));
      }
      if (gap > 0 && R.draft && (!mates || o.team === r.team) && !(o.ai && o.ai.restT > 0)) parts.push(wakeShelter(gap, (p - (l - ai.side)) * LANE_W));
    }
    return SETTLE.crowd * crowd - SETTLE.shelter * combineShelter(parts) + SETTLE.move * Math.abs(l - here) + (hazardAhead(race, r, l) ? 10 : 0);
  };
  let best = here,
    bestScore = score(here);
  const steps: number[] = [];
  for (let k = -SETTLE.reach; k <= SETTLE.reach; k++) if (k) steps.push(k * SETTLE.grain);
  for (let k = -3; k <= 3; k++) if (k) steps.push(k * SETTLE.fine);
  for (const d of steps) {
    const l = clamp(here + d, -ROAD_EDGE, ROAD_EDGE);
    if (l === here) continue;
    // a place has to beat the one held by its hysteresis: a trim's, or a move's
    const c = score(l) + (Math.abs(d) <= 3 * SETTLE.fine + 1e-9 ? SETTLE.trim : SETTLE.gain);
    if (c < bestScore) {
      best = l;
      bestScore = c;
    }
  }
  // the wheel that moved away is a place of its own to consider, however far: the move pays for itself in shelter (in a
  // small group: in a bunch the next wheel is right there, and following would ripple down the pack)
  if (
    (REACT.bunch || !ai.bunch) &&
    ai.wheel &&
    !(ai.wheel.ai && ai.wheel.ai.restT > 0) &&
    Math.abs(ai.wheel.latV) > REACT.moving &&
    Math.abs(ai.wheel.targetLane - here) > REACT.far
  ) {
    const to = clamp(ai.wheel.targetLane + ai.side, here - ROOM.reach, here + ROOM.reach),
      l = clamp(to, -ROAD_EDGE, ROAD_EDGE),
      c = score(l) - SETTLE.move * Math.abs(l - here);
    if (c < bestScore) best = l;
  }
  r.targetLane = best;
}

/**
 * Taking the wheel: power is brought down to what keeps the target gap behind the rider ahead.
 * A clearly stronger bot that stays stuck for a few seconds pulls out into a free spot and moves up.
 */
function holdWheel(race: Race, r: Rider, want: number, dt: number): number {
  const ai = r.ai!;
  // moving up: the aim is frozen (settle), the wheel in hand let go; whoever is ahead on the new line is still a wheel to settle on
  if (ai.passT > 0) {
    ai.passT -= dt;
    ai.wheel = null;
  }
  const inLane = wheelAhead(race, r, WHEEL.range, r.lane, false, SLOT);
  // the wheel in hand is let go when it is gone, passed, out of reach, or easing off after its pull
  if (ai.wheel) {
    const g = gapOf(race, r, ai.wheel);
    if (ai.wheel.out || ai.wheel.fx.crash || g <= 0 || g > WHEEL.range + 4 || (ai.wheel.ai && ai.wheel.ai.restT > 0)) ai.wheel = null;
  }
  if (inLane) ai.wheel = inLane.o;
  // while following a wheel that moved over, keep its pace instead of surging past it in the wind
  const held = ai.wheel ? gapOf(race, r, ai.wheel) : 0;
  const w = inLane ?? (ai.wheel && held < WHEEL.range ? { o: ai.wheel, gap: held } : null);
  if (!w) {
    ai.stuck = 0;
    return moveUp(race, r, want);
  }
  // an opponent's wheel gives no shelter when drafting is for teammates only: ride your own pace and get past
  if (race.rules.teams?.draftTeamOnly && w.o.team !== r.team) {
    ai.wheel = null;
    ai.stuck = inLane ? ai.stuck + dt : 0;
    if (ai.stuck > 1 && pullOut(race, r)) {
      ai.passT = 5;
      ai.stuck = 0;
    }
    return want;
  }
  const pt = trackAt(race.track, r.s),
    m = r.mass + BIKE_KG;
  const vt = Math.max(0, w.o.v + clamp((w.gap - WHEEL.gap) * WHEEL.pull, -2, CLOSE.speed));
  const cda = r.cda * r.pose * (1 - r.draft) * airFactor(race.track.alt0 + pt.y);
  // force to ride at vt, plus the force that gets there in ~2 s
  const force = resistance(r.v, pt.grade, m, cda) + (m * INERTIA * (vt - r.v)) / 2;
  const need = Math.max(0, force * Math.max(r.v, 1.5));
  // about to ride into the wheel: a touch of the brakes, in proportion, so the bot settles on it instead of overlapping
  if (w.gap < WHEEL.gap && r.v > w.o.v) r.brake = (r.v - w.o.v) * WHEEL.feather;
  // a gap opening ahead is shut with a dig, while the reserve lasts (see CLOSE)
  const cap = w.gap > CLOSE.from && ai.w / ai.wp > CLOSE.reserve ? Math.max(want, r.ftp * CLOSE.boost) : want;
  if (cap <= need || !inLane || ai.passT > 0) {
    ai.stuck = 0;
    return Math.min(cap, need);
  }
  // held back by the wheel, with watts to spare: after a moment (sooner for a keen temperament) the bot moves up into room
  // beside, the way a bunch fills in from behind and riders surf up its sides; at the front, into the wind, only a rider
  // able to hold the leader's speed without shelter goes
  ai.stuck += dt;
  if (ai.stuck > MOVE_UP.wait + (1 - ai.aggr) * MOVE_UP.patience) {
    const alone = resistance(w.o.v + 0.5, pt.grade, m, cda / (1 - r.draft || 1)) * (w.o.v + 0.5);
    if (pullOut(race, r, want > alone)) {
      ai.passT = MOVE_UP.hold;
      ai.stuck = 0;
      return want;
    }
    ai.stuck = 0;
  }
  return need;
}

/** No wheel straight ahead, but a rider just ahead and beside: ride up their flank at a little more than their speed (see PACE). */
function moveUp(race: Race, r: Rider, want: number): number {
  const mates = race.rules.teams?.draftTeamOnly;
  let o: Rider | null = null,
    gap: number = PACE.range;
  for (const c of race.riders) {
    if (c === r || c.out || c.fx.crash || (mates && c.team !== r.team) || Math.abs(c.lane - r.lane) > PACE.width) continue;
    const g = gapOf(race, r, c);
    if (g > 0 && g < gap) {
      gap = g;
      o = c;
    }
  }
  if (!o) return want;
  const pt = trackAt(race.track, r.s),
    m = r.mass + BIKE_KG,
    vt = o.v + Math.min(PACE.surge, gap * 0.15);
  const force = resistance(r.v, pt.grade, m, r.cda * r.pose * (1 - r.draft) * airFactor(race.track.alt0 + pt.y)) + (m * INERTIA * (vt - r.v)) / 2;
  return Math.min(want, Math.max(0, force * Math.max(r.v, 1.5)));
}

/**
 * Moves the bot to the nearest line beside with room to move up: nobody level with it, and a bike's room before the next
 * rider ahead on that line (MOVE_UP.room). A line with nobody ahead at all is the wind: only with `front`. False when every
 * line is taken.
 */
function pullOut(race: Race, r: Rider, front = true): boolean {
  const free = spots(r).filter(l => {
    if (Math.abs(l - r.targetLane) > ROOM.reach || alongside(race, r, l, 1.8) || hazardAhead(race, r, l)) return false;
    const next = wheelAhead(race, r, WHEEL.pass, l, true);
    return next ? next.gap >= MOVE_UP.room : front;
  });
  if (!free.length) return false;
  r.targetLane = free[0];
  return true;
}

/** Hazard or fallen rider on `lane` within the next `range` metres: a bot sees a crash only when it looks up, so a wheel too close still goes down. */
function hazardAhead(race: Race, r: Rider, lane: number, range = 30): boolean {
  const on = (z: { s: number; lane: number }) => {
    const d = mod(z.s - r.s, race.track.L);
    return d > 0 && d < range && Math.abs(z.lane - lane) < 0.6;
  };
  return race.hazards.some(on) || race.riders.some(o => o !== r && !!o.fx.crash && !o.out && on(o));
}

/** A bot's pull at the front of a group (see PULL). */
function relay(race: Race, r: Rider, p: number, dt: number): number {
  const ai = r.ai!;
  if (ai.restT > 0) {
    ai.restT -= dt;
    return p * PULL.ease;
  }
  if (wheelAhead(race, r, WHEEL.range, r.lane, false, SLOT)) {
    ai.pullT = 0;
    return p;
  }
  const followers = race.riders.filter(o => {
    if (o === r || o.out) return false;
    const g = -gapOf(race, r, o);
    return g > 0 && g < 4;
  });
  const followed = followers.some(o => Math.abs(o.lane - r.lane) < 0.5);
  // a follower who changes line for a moment does not cancel the current pull
  if (!followed) {
    ai.pullT = Math.max(0, ai.pullT - dt / 2);
    return p;
  }
  ai.pullT += dt;
  if (ai.pullT > PULL.base + ai.aggr * PULL.spread) {
    // end of the pull: pull off to a free spot clear of the riders on the wheel, and let the group go by
    const free = spots(r).filter(
      l =>
        Math.abs(l - r.lane) > 0.5 &&
        Math.abs(l - r.lane) <= ROOM.reach &&
        followers.every(o => Math.abs(o.lane - l) >= ROOM.block) &&
        !alongside(race, r, l, 3),
    );
    if (free.length) {
      r.targetLane = free[0];
      ai.pullT = 0;
      ai.restT = PULL.rest;
      ai.passT = 0;
      return p * PULL.ease;
    }
  }
  return p * (ai.bunch ? PULL.bunch : PULL.boost);
}

export function makeBotAI(race: Race, mass: number, ftp: number, spec?: BotSpecialty, rnd = true): BotAI {
  const wp = WPRIME_PER_KG * clamp(mass || 75, 40, 120) * (spec ? WPRIME_SPEC[spec] : 1);
  const pmax = Math.max(1, ftp) * (spec ? PMAX_SPEC[spec] : PMAX_RATIO);
  const side = (rnd ? SIDE.min + race.rnd() * (SIDE.max - SIDE.min) : (SIDE.min + SIDE.max) / 2) * (rnd && race.rnd() < 0.5 ? -1 : 1);
  const drawn = (a: number, b: number) => (rnd ? a + race.rnd() * (b - a) : (a + b) / 2);
  const wander: [number, number, number, number] = [drawn(0, 6.283), drawn(0, 6.283), drawn(SWAY.slow[0], SWAY.slow[1]), drawn(SWAY.slower[0], SWAY.slower[1])];
  return {
    noise: 0,
    laneT: rnd ? race.rnd() * 2 : 0,
    hold: 0,
    aggr: rnd ? 0.4 + race.rnd() * 0.6 : 0.5,
    spec,
    w: wp,
    wp,
    pmax,
    passT: 0,
    stuck: 0,
    pullT: 0,
    restT: 0,
    wheel: null,
    side,
    wander,
    sway: 0,
    bend: 0,
    bunch: false,
  };
}
